import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Icon,
  Popover,
  PopoverTrigger,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { Mic, ShareScreen } from '@components/icons'
import {
  AUDIO_SHARE_SCREEN_WARNING_MESSAGE,
  SCREEN_SHARE_WARNING_MESSAGE,
  SCREEN_SHARE_WARNING_MESSAGE_WINDOWS,
  START_RECORDING_WARNING_MESSAGE
} from '@handlers/sessions/constants'
import { useAudioCapture, usePanelManager } from '@hooks'
import * as clinicianTracking from '@lib/clinician-tracking'
import WarningIcon from '@material-ui/icons/Warning'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import type { WarningType } from '../../../../context/sessions-context/types'
import { isWindows } from '../../../../context/sessions-context/utils'
import { formatTimeForSessionTimer } from '../../util'
import { MicrophoneSelectorV2 } from '../microphone-selector-v2'
import { MobileTelehealthToggle } from '../mobile-telehealth-toggle'
import { WarningPopover } from '../shared/warning-popover'
import StopRecordingAfter from '../stop-recording-after'
import { TelehealthToggle } from '../telehealth-toggle'

interface NotetakerSettingsProps {
  hasStartedRecording: boolean
  recordingDuration: number
  isDictation: boolean
  isMobileAssistOpen: boolean
  isStopRecordingAfterEnabled: boolean
  setIsStopRecordingAfterEnabled: (value: boolean) => void
  stopRecordingAfter: string
  setStopRecordingAfter: (value: string) => void
  needAudioSharing: boolean
  showSafariWarning: boolean
  isRecordingLoading: boolean
  handleStartRecording: () => void
  client: any
}

const WARNING_TIMEOUT = 10000
const INTERACTION_TIMEOUT = 10000

const useRecordingWarning = (
  hasStartedRecording: boolean,
  hasDetectedAudio: boolean,
  client: any
) => {
  const [recordingWarningDismissed, setRecordingWarningDismissed] =
    useState(false)
  const [recordingWarningEligible, setRecordingWarningEligible] =
    useState(false)
  const [recordingWarningTimer, setRecordingWarningTimer] =
    useState<NodeJS.Timeout | null>(null)
  const [lastInteractionAt, setLastInteractionAt] = useState<number>(Date.now())

  const shouldBeEligible = useMemo(
    () =>
      !hasStartedRecording &&
      hasDetectedAudio &&
      !client?.is_demo &&
      !recordingWarningDismissed,
    [hasStartedRecording, hasDetectedAudio, client, recordingWarningDismissed]
  )

  useEffect(() => {
    if (shouldBeEligible && !recordingWarningEligible) {
      setRecordingWarningEligible(true)
    } else if (!shouldBeEligible && recordingWarningEligible) {
      setRecordingWarningEligible(false)
    }
  }, [shouldBeEligible, recordingWarningEligible])

  useEffect(() => {
    if (recordingWarningEligible) {
      if (recordingWarningTimer) clearTimeout(recordingWarningTimer)

      const timer = setTimeout(
        () => setRecordingWarningTimer(null),
        WARNING_TIMEOUT
      )
      setRecordingWarningTimer(timer)

      return () => clearTimeout(timer)
    } else if (recordingWarningTimer) {
      clearTimeout(recordingWarningTimer)
      setRecordingWarningTimer(null)
    }
  }, [recordingWarningEligible])

  const shouldShowRecordingWarning = useMemo(
    () =>
      recordingWarningEligible &&
      recordingWarningTimer === null &&
      Date.now() - lastInteractionAt > INTERACTION_TIMEOUT,
    [recordingWarningEligible, recordingWarningTimer, lastInteractionAt]
  )

  useEffect(() => {
    const handleInteraction = () => {
      // If the warning is currently showing, don't update the last interaction time so that we don't dismiss the warning
      if (shouldShowRecordingWarning) return

      setLastInteractionAt(Date.now())

      if (recordingWarningTimer) {
        clearTimeout(recordingWarningTimer)
        setRecordingWarningTimer(null)
      }
    }

    document.addEventListener('click', handleInteraction)
    document.addEventListener('touchstart', handleInteraction)

    return () => {
      document.removeEventListener('click', handleInteraction)
      document.removeEventListener('touchstart', handleInteraction)
    }
  }, [recordingWarningTimer, shouldShowRecordingWarning])

  const dismissWarning = useCallback(() => {
    setRecordingWarningDismissed(true)
    setRecordingWarningEligible(false)
    if (recordingWarningTimer) {
      clearTimeout(recordingWarningTimer)
      setRecordingWarningTimer(null)
    }
  }, [recordingWarningTimer])

  return {
    shouldShowRecordingWarning,
    dismissWarning
  }
}

const useButtonWidth = () => {
  const [buttonWidth, setButtonWidth] = useState<number>(0)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const updateWidth = () => {
      if (buttonRef.current) {
        setButtonWidth(buttonRef.current.offsetWidth)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  return { buttonWidth, buttonRef }
}

const WARNING_CONFIG = {
  recording: {
    type: 'info' as const,
    message: START_RECORDING_WARNING_MESSAGE
  },
  screenshare: {
    type: 'error' as const,
    message: isWindows()
      ? SCREEN_SHARE_WARNING_MESSAGE_WINDOWS
      : SCREEN_SHARE_WARNING_MESSAGE
  },
  'audio-stream': {
    type: 'error' as const,
    message: AUDIO_SHARE_SCREEN_WARNING_MESSAGE
  },
  mic: {
    type: 'warning' as const,
    message: 'No microphone audio detected. Please check your microphone.'
  },
  tab: {
    type: 'warning' as const,
    message:
      'No tab audio detected. Please check that audio is playing in the shared tab.'
  }
} as const

export const NotetakerSettings = ({
  hasStartedRecording,
  recordingDuration,
  isDictation,
  isMobileAssistOpen,
  isStopRecordingAfterEnabled,
  setIsStopRecordingAfterEnabled,
  stopRecordingAfter,
  setStopRecordingAfter,
  needAudioSharing,
  showSafariWarning,
  isRecordingLoading,
  handleStartRecording,
  client
}: NotetakerSettingsProps) => {
  const {
    hasDetectedAudio,
    activeWarning,
    setActiveWarning,
    isUsingHeadphones,
    telehealthAudioSource,
    isTelehealthSourceRequiredEnabled,
    qualityToggle
  } = useAudioCapture()
  const { isMobileAssistOpen: panelMobileAssistOpen } = usePanelManager()
  const {
    isOpen: isPopoverOpen,
    onClose: closePopover,
    onOpen: openPopover
  } = useDisclosure()

  const { shouldShowRecordingWarning, dismissWarning } = useRecordingWarning(
    hasStartedRecording,
    hasDetectedAudio,
    client
  )
  const { buttonWidth, buttonRef } = useButtonWidth()

  useEffect(() => {
    if (shouldShowRecordingWarning && activeWarning !== 'recording') {
      setActiveWarning('recording')
    } else if (!shouldShowRecordingWarning && activeWarning === 'recording') {
      setActiveWarning(null)
    }
  }, [shouldShowRecordingWarning, activeWarning, setActiveWarning])

  useEffect(() => {
    if (!!activeWarning && !panelMobileAssistOpen) {
      if (!isPopoverOpen) {
        openPopover()

        // Track warning shown event
        clinicianTracking.trackEvent(
          'Warning Shown',
          {
            component: 'NotetakerSettings',
            warningType: activeWarning,
            warningMessage: WARNING_CONFIG[activeWarning]?.message || '',
            sessionId: client?.sessionId || ''
          },
          [
            clinicianTracking.Destinations.Segment,
            clinicianTracking.Destinations.Mixpanel
          ]
        )
      }
    } else if (isPopoverOpen) {
      closePopover()
    }
  }, [
    activeWarning,
    panelMobileAssistOpen,
    isPopoverOpen,
    openPopover,
    closePopover,
    client
  ])

  const handleDismissWarning = useCallback(
    (warningType: WarningType) => {
      setActiveWarning(null)
      if (warningType === 'recording') dismissWarning()
      if (isPopoverOpen) closePopover()
    },
    [setActiveWarning, dismissWarning, isPopoverOpen, closePopover]
  )

  // Check if button should be disabled based on the specified conditions
  const isButtonDisabled = useMemo(() => {
    return (
      isUsingHeadphones && telehealthAudioSource === 'desktop' && !isWindows()
    )
  }, [isUsingHeadphones, telehealthAudioSource])

  let buttonText = isDictation
    ? 'Dictate'
    : needAudioSharing
      ? isWindows()
        ? 'Share telehealth audio + begin recording'
        : 'Share telehealth audio + begin recording'
      : 'Start Recording'

  if (
    isTelehealthSourceRequiredEnabled &&
    telehealthAudioSource === null &&
    qualityToggle
  ) {
    buttonText = 'Share telehealth audio + begin recording'
  }

  return (
    <Stack
      gap="13px"
      alignItems="center"
      textAlign="center"
      maxW={{ base: '100%', sm: '400px' }}
      w="100%"
    >
      <Box>
        <Text fontSize="16px" style={{ marginTop: 0 }} color="#757575">
          Not yet recording
        </Text>
        <Text
          width="100%"
          color={hasStartedRecording ? 'primary' : '#2D54E833'}
          fontSize="88px"
          fontWeight="300"
        >
          {formatTimeForSessionTimer(recordingDuration)}
        </Text>
      </Box>

      <MicrophoneSelectorV2 />
      {isMobile ? (
        <MobileTelehealthToggle />
      ) : (
        <TelehealthToggle isDictation={isDictation} />
      )}

      <Popover
        isOpen={isPopoverOpen && !isMobileAssistOpen}
        placement="bottom"
        autoFocus={false}
        closeOnBlur={false}
        closeOnEsc={false}
      >
        <PopoverTrigger>
          <Button
            ref={buttonRef}
            bg={isButtonDisabled ? 'gray.300' : 'primary'}
            color={isButtonDisabled ? 'gray.900' : 'white'}
            fontWeight="400"
            height="48px"
            width="100%"
            variant="body"
            isLoading={isRecordingLoading}
            leftIcon={
              isDictation || !needAudioSharing ? (
                <Mic width="16px" fill="white" />
              ) : (
                <ShareScreen width="16px" fill="white" />
              )
            }
            data-click-event-name="Session -> Clicked Start Recording"
            data-click-text={buttonText}
            onClick={handleStartRecording}
            _hover={
              isButtonDisabled
                ? {}
                : {
                    bg: 'primary',
                    opacity: 0.8
                  }
            }
            _focus={{
              outline: 'none'
            }}
            id="pendo-session-panel-start-recording"
            disabled={isButtonDisabled}
            sx={{
              transition: 'transform 100ms cubic-bezier(0.23, 1, 0.32, 1)',
              '&:active': { transform: 'scale(0.98)' }
            }}
          >
            {buttonText}
          </Button>
        </PopoverTrigger>

        {activeWarning && WARNING_CONFIG[activeWarning] && (
          <WarningPopover
            type={WARNING_CONFIG[activeWarning].type}
            width={buttonWidth}
            message={WARNING_CONFIG[activeWarning].message}
            onClose={() => handleDismissWarning(activeWarning)}
          />
        )}
      </Popover>

      {!isDictation && (
        <StopRecordingAfter
          isEnabled={isStopRecordingAfterEnabled}
          setIsEnabled={setIsStopRecordingAfterEnabled}
          value={stopRecordingAfter}
          onChange={setStopRecordingAfter}
          useV2Styles={true}
        />
      )}

      {showSafariWarning && (
        <Alert
          status="error"
          bg="#FFF2F2"
          borderRadius="8px"
          p={4}
          border="1px solid rgba(40, 40, 40, 0.05)"
        >
          <Box mr={2} w="20px" h="20px">
            <Icon
              as={WarningIcon}
              color="red.500"
              mr="16px"
              mt="2px"
              boxSize={6}
            />
          </Box>
          <AlertDescription color="#282828" textAlign="left" marginLeft="16px">
            <b>Important:</b> Due to a limitation of the Safari browser, the AI
            Notetaker feature will not be able to capture participant audio
            duing a telehealth session. For the best results, please use Chrome.
          </AlertDescription>
        </Alert>
      )}
    </Stack>
  )
}
